import { useMutation } from "@tanstack/react-query";
import { ArrowUpRight, Check, CreditCard, Gauge } from "lucide-react";
import { useState } from "react";
import { Badge, Button, Callout, ErrorState } from "@/design";
import { ApiError, api, idem, unwrap, useChooseOnboardingPlan, useMyOnboarding, useWorkspace } from "@/lib/api";
import { cn } from "@/lib/cn";
import { config, docsUrl } from "@/lib/config";
import { formatMoney } from "@/lib/format";
import { StepHeading } from "./OnboardingFrame";
import { signupPlans, VAT_RATE, type SignupPlanId } from "./plans";

/** `billingEnabled` is false while card payments are not switched on (early access): everyone starts on the trial. */
export function PlanStep({ workspace, billingEnabled, onChosen }: { workspace: string; billingEnabled: boolean; onChosen: () => void }) {
  const [choice, setChoice] = useState<SignupPlanId>("trial");
  const [needsCard, setNeedsCard] = useState(false);
  const plan = signupPlans.find((p) => p.id === choice)!;
  const ws = useWorkspace(workspace);
  const mine = useMyOnboarding();
  // The workspace you signed up with takes its plan through onboarding; any later workspace through billing.
  const viaOnboarding = !!mine.data && !!ws.data && mine.data.workspace_id === ws.data.id && !mine.data.plan_chosen;
  const choose = useChooseOnboardingPlan();
  // Business starts with a conversation (DPA, SSO, evidence pack), so it begins on the trial.
  const talkFirst = choice === "business";
  const paidLater = !billingEnabled && choice !== "trial" && !talkFirst;

  const change = useMutation({
    mutationFn: (p: Exclude<SignupPlanId, "trial">) => unwrap(api.POST("/v1/workspaces/{workspace}/billing/plan", { params: { path: { workspace }, header: idem() }, body: { plan: p } })),
    onSuccess: () => onChosen(),
    onError: (e) => {
      if (e instanceof ApiError && (e.status === 402 || e.code === "payment_required")) setNeedsCard(true);
    },
  });
  const portal = useMutation({
    mutationFn: () => unwrap(api.POST("/v1/workspaces/{workspace}/billing/portal", { params: { path: { workspace }, header: idem() } })),
    onSuccess: (s) => window.location.assign(s.url),
  });

  function confirm() {
    setNeedsCard(false);
    if (viaOnboarding) {
      const p = choice === "trial" || paidLater || talkFirst ? "trial" : choice;
      return choose.mutate(p, {
        onSuccess: () => onChosen(),
        // Already chosen (say, in another tab): nothing more to do here.
        onError: (e) => e instanceof ApiError && e.status === 409 && onChosen(),
      });
    }
    if (choice === "trial" || paidLater || talkFirst) return onChosen(); // New workspaces start on the trial; nothing to change.
    change.mutate(choice);
  }
  const busy = change.isPending || choose.isPending;
  const mailto = `mailto:${config.supportEmail}?subject=${encodeURIComponent(`Guv Business for ${ws.data?.name ?? workspace}`)}`;

  return (
    <div>
      <StepHeading eyebrow="Last step" title="Choose how to start">
        Every plan includes usage, so a small app often costs nothing beyond the plan. Switch or cancel whenever you like.
      </StepHeading>

      <fieldset className="mt-8">
        <legend className="sr-only">Plan</legend>
        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
          {signupPlans.map((p) => {
            const selected = p.id === choice;
            return (
              <label
                key={p.id}
                className={cn(
                  "relative flex cursor-pointer flex-col rounded-xl border bg-surface p-4 shadow-xs transition-[border-color,box-shadow] duration-150",
                  "has-[:focus-visible]:ring-3 has-[:focus-visible]:ring-focus/40",
                  selected ? "border-accent ring-1 ring-accent" : "border-border hover:border-border-strong",
                )}
              >
                <input type="radio" name="plan" value={p.id} checked={selected} onChange={() => setChoice(p.id)} className="sr-only" />
                <div className="flex items-center justify-between gap-2">
                  <span className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
                    <span className="text-md font-semibold text-text">{p.name}</span>
                    {p.featured && (
                      <Badge size="sm" tone="ok" variant="outline">
                        Best for teams
                      </Badge>
                    )}
                  </span>
                  <span
                    aria-hidden="true"
                    className={cn("flex size-5 items-center justify-center rounded-full border transition-colors", selected ? "border-accent bg-accent text-accent-fg" : "border-border-strong")}
                  >
                    {selected && <Check className="size-3" strokeWidth={3} />}
                  </span>
                </div>
                <div className="mt-3 flex items-baseline gap-1.5">
                  <span className="font-display text-3xl font-semibold tabular-nums text-text">{formatMoney(p.fee, { whole: true })}</span>
                  <span className="text-sm text-muted">{p.feeNote}</span>
                </div>
                <p className="mt-0.5 text-xs text-subtle tabular-nums">{p.fee > 0 ? `${formatMoney(p.fee * (1 + VAT_RATE), { whole: true })} with VAT` : billingEnabled ? "Card needed, nothing charged" : "No card needed"}</p>
                <p className="mt-3 text-sm font-medium text-accent-text">{p.included}</p>
                <p className="mt-1 text-sm text-muted">{p.blurb}</p>
                <ul className="mt-3 space-y-1.5 border-t border-border pt-3 text-sm text-muted">
                  {p.points.map((pt) => (
                    <li key={pt} className="flex gap-2">
                      <Check className="mt-0.5 size-3.5 shrink-0 text-ok-text" aria-hidden="true" />
                      <span>{pt}</span>
                    </li>
                  ))}
                </ul>
              </label>
            );
          })}
        </div>
      </fieldset>

      <div className="mt-5 grid gap-3 text-sm text-muted sm:grid-cols-2">
        <p className="flex gap-2.5 rounded-lg bg-surface-2 px-3.5 py-3">
          <Gauge className="mt-0.5 size-4 shrink-0 text-accent-text" aria-hidden="true" />
          <span>
            {plan.defaultCap ? (
              <>
                You start with a <span className="font-medium text-text">{formatMoney(plan.defaultCap, { whole: true })} monthly spend cap</span>, so a busy month can't become a surprise bill.
              </>
            ) : plan.id === "trial" ? (
              <>The trial comes with £5 of credit. You won't be charged anything until you choose a paid plan.</>
            ) : (
              <>Set a spend cap before you deploy and we'll never charge you more than it.</>
            )}
          </span>
        </p>
        <p className="flex gap-2.5 rounded-lg bg-surface-2 px-3.5 py-3">
          <CreditCard className="mt-0.5 size-4 shrink-0 text-accent-text" aria-hidden="true" />
          <span>
            {!billingEnabled
              ? "No card needed during early access. Prices exclude VAT at 20%, and invoices are VAT-correct."
              : plan.id === "trial"
                ? "We'll ask for a card before your first deploy. It keeps out people who'd misuse free computing, and it's not charged during the trial."
                : "Prices exclude VAT at 20%. You'll get a proper VAT invoice each month, from a UK company."}
          </span>
        </p>
      </div>

      {talkFirst && (
        <Callout
          tone="info"
          className="mt-5"
          title="Business starts with a quick conversation"
          actions={
            <Button asChild size="sm">
              <a href={mailto}>
                Email us about Business
                <ArrowUpRight aria-hidden="true" />
              </a>
            </Button>
          }
        >
          We set up single sign-on, the DPA and the evidence pack with you. Start on the trial now and we'll move your workspace across; nothing you build will need to change.
        </Callout>
      )}
      {paidLater && (
        <Callout tone="info" className="mt-5" title={`${plan.name} opens soon`}>
          Card payments aren't switched on yet. Start on the trial now and we'll email you the moment you can move to {plan.name}; nothing you build will need to change.
        </Callout>
      )}
      {needsCard && (
        <Callout
          tone="warn"
          className="mt-5"
          title={`Add a card to start ${plan.name}`}
          actions={
            <>
              <Button size="sm" variant="primary" loading={portal.isPending} onClick={() => portal.mutate()}>
                Add a card
              </Button>
              <Button size="sm" onClick={() => (setChoice("trial"), setNeedsCard(false))}>
                Start with the trial
              </Button>
            </>
          }
        >
          Paid plans need a card on file. You can add one securely now, or start with the trial and choose a plan later.
        </Callout>
      )}
      {change.isError && !needsCard && <ErrorState error={change.error} className="mt-5" />}
      {choose.isError && !(choose.error instanceof ApiError && choose.error.status === 409) && <ErrorState error={choose.error} className="mt-5" />}
      {portal.isError && <ErrorState error={portal.error} className="mt-5" />}

      <div className="mt-7 flex flex-col-reverse items-stretch gap-3 sm:flex-row sm:items-center sm:justify-between">
        <a href={docsUrl("/pricing")} className="rounded-sm text-center text-sm font-medium text-accent-text underline-offset-4 hover:underline">
          Compare every plan in detail
        </a>
        <Button variant="primary" size="lg" loading={busy} disabled={mine.isPending || ws.isPending} onClick={confirm} className="sm:min-w-48">
          {paidLater || talkFirst ? "Start on the trial" : plan.cta}
        </Button>
      </div>
      <p className="mt-4 text-xs text-subtle sm:text-right">
        Need single-tenant hosts for special-category data?{" "}
        <a href={`mailto:${config.supportEmail}?subject=${encodeURIComponent("Guv Dedicated")}`} className="underline underline-offset-2 hover:text-text">
          Talk to us about Dedicated
        </a>
        .
      </p>
    </div>
  );
}
